import {
  Cached as CachedIcon,
  Close as CloseIcon,
  Done as DoneIcon,
  KeyboardArrowLeft,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { LoadingButton } from "../../../common/components/LoadingButton";
import { useActionRequest } from "../../../common/hooks/api";
import { NetworkRequest } from "../../../common/models";
import { MutationOrNetworkState } from "../../../common/models/NetworkRequest";
import { getCompliantProgressActions } from "../../../common/reducers/getCompliantProgressSlice";
import { Api } from "../../../common/service/Api";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationPublicId } from "../../../common/service/server/Types";
import { InlineLoading } from "../../components";
import { Error as OrganizationError } from "../../copy/organization";
import { usePrimaryOrganizationId } from "../../hooks";
import {
  GetCompliantStep,
  StepComparison,
  useNextStep,
  usePrevStep,
} from "../../surveys/steps/GetCompliantSteps";
import {
  UseNextSurveyStepHook,
  UsePrevSurveyStepHook,
} from "../../surveys/steps/CatchupSurveyStepManager";

interface ProgressFooterProps {
  content?: ReactNode | string;
  actions?: ReactNode | string;
  saveRequests?: MutationOrNetworkState[] | MutationOrNetworkState;
}
export const ProgressFooter = ({ content, actions }: ProgressFooterProps) => {
  return (
    <div className="progress-footer">
      {content && <span className="progress-footer-content">{content}</span>}
      {actions && <span className="progress-footer-actions">{actions}</span>}
    </div>
  );
};

type ProgressFooterSaveProps = {
  request?: MutationOrNetworkState | MutationOrNetworkState[];
};

export const ProgressFooterSaveNotification: React.FC<ProgressFooterSaveProps> = ({ request }) => {
  if (!request) {
    return null;
  }

  if (!Array.isArray(request)) {
    request = [request];
  }

  const unstarted = NetworkRequest.areUnstarted(...request);
  const anyErrors = NetworkRequest.anyFailed(...request);
  const running = NetworkRequest.anyRunning(...request);

  if (unstarted) {
    return null;
  }

  return (
    <>
      <span className="progress-footer-save-icon">
        {running ? <CachedIcon /> : anyErrors ? <CloseIcon /> : <DoneIcon />}
      </span>
      <span className="progress-footer-save-text">
        {running ? "Saving" : anyErrors ? "Error Saving" : "Saved"}
      </span>
    </>
  );
};

type ProgressFooterBackProps = {
  label?: React.ReactNode;
};

export const ProgressFooterBack: React.FC<ProgressFooterBackProps> = ({ label = "Back" }) => {
  return (
    <span className="flex-center-vertical">
      <KeyboardArrowLeft /> {label}
    </span>
  );
};

interface ProgressFooterActionsProps {
  nextContent?: ReactNode | string;
  nextLoading?: boolean;
  nextOnClick?: () => void;
  nextUrl?: string;
  nextTooltip?: string;
  nextDisabledTooltip?: string;
  nextDisabled?: boolean;
  nextForm?: string;
  nextType?: "button" | "submit" | "reset";
  hideNext?: boolean;
  prevContent?: ReactNode | string;
  prevTooltip?: string;
  prevDisabledTooltip?: string;
  prevOnClick?: () => void;
  prevUrl?: string;
  prevDisabled?: boolean;
  prevLoading?: boolean;
}
export const ProgressFooterActions = ({
  nextContent,
  nextOnClick,
  nextUrl,
  nextTooltip,
  nextDisabled = false,
  nextLoading = false,
  nextDisabledTooltip,
  nextForm,
  nextType,
  prevContent = (
    <span className="flex-center-vertical">
      <KeyboardArrowLeft /> Back
    </span>
  ),
  prevTooltip,
  prevDisabledTooltip,
  prevOnClick,
  prevUrl,
  prevDisabled = false,
  prevLoading = false,
  hideNext = false,
}: ProgressFooterActionsProps) => {
  if (nextDisabled && nextDisabledTooltip) {
    nextTooltip = nextDisabledTooltip;
  }

  if (prevDisabled && prevDisabledTooltip) {
    prevTooltip = prevDisabledTooltip;
  }

  const prevComponent = prevDisabled && prevTooltip ? "div" : undefined;
  const nextComponent = nextDisabled && nextTooltip ? "div" : undefined;

  const hasPrevAction = Boolean(prevOnClick || prevUrl);
  const prevButton = hasPrevAction && prevContent && (
    <LoadingButton
      className="progress-footer-actions--prev-btn mr-md min-width-256"
      color="primary"
      onClick={prevDisabled ? undefined : prevOnClick}
      href={prevUrl}
      disabled={prevDisabled}
      size="large"
      variant="text"
      loading={prevLoading}
      component={prevComponent}
    >
      {prevContent}
    </LoadingButton>
  );

  const hasNextAction = Boolean(nextOnClick || nextUrl || nextType === "submit");
  const nextButton =
    hasNextAction && nextContent && nextComponent ? (
      <LoadingButton
        className="progress-footer-actions--next-btn min-width-256"
        color="primary"
        variant="contained"
        onClick={nextDisabled ? undefined : nextOnClick}
        href={nextUrl}
        disabled={nextDisabled}
        loading={nextLoading}
        size="large"
        component={nextComponent}
      >
        {nextContent}
      </LoadingButton>
    ) : (
      <LoadingButton
        className="progress-footer-actions--next-btn min-width-256"
        color="primary"
        variant="contained"
        onClick={nextDisabled ? undefined : nextOnClick}
        href={nextUrl}
        disabled={nextDisabled}
        loading={nextLoading}
        size="large"
        type={nextType}
        form={nextForm}
      >
        {nextContent}
      </LoadingButton>
    );

  return (
    <>
      {prevButton &&
        (prevTooltip ? (
          <Tooltip title={prevTooltip} placement="top-end">
            <span>{prevButton}</span>
          </Tooltip>
        ) : (
          prevButton
        ))}
      {!hideNext &&
        nextButton &&
        (nextTooltip ? (
          <Tooltip title={nextTooltip} placement="top-end">
            <span>{nextButton}</span>
          </Tooltip>
        ) : (
          nextButton
        ))}
    </>
  );
};

interface GetCompliantFooterProps {
  presentStep: GetCompliantStep;
  nextLabel?: string | ReactNode;

  nextUrl?: string;
  nextOnClick?: () => Promise<void>;
  nextDisabled?: boolean;
  nextDisabledTooltip?: string;
  nextTooltip?: string;

  noProgress?: boolean;
  prevLabel?: string | ReactNode;
  prevUrl?: string;
  prevOnClick?: () => Promise<void>;
  prevSkip?: GetCompliantStep[];
  nextSkip?: GetCompliantStep[];
  content?: ReactNode | string;
  saveRequests?: MutationOrNetworkState[] | MutationOrNetworkState;
  recipients?: OrganizationDataRecipientDto[];
}
export const GetCompliantFooter: React.FC<GetCompliantFooterProps> = ({
  presentStep,
  nextLabel = "Next",
  nextDisabled,
  nextDisabledTooltip,
  nextTooltip,
  nextOnClick,
  nextUrl,
  nextSkip,
  noProgress = false,
  prevLabel = (
    <span className="flex-center-vertical">
      <KeyboardArrowLeft /> Back
    </span>
  ),
  prevUrl,
  prevOnClick,
  prevSkip,
  content,
  saveRequests,
  recipients,
}) => {
  if (typeof prevLabel == "string" || prevLabel instanceof String) {
    prevLabel = (
      <span className="flex-center-vertical">
        <KeyboardArrowLeft /> {prevLabel}
      </span>
    );
  }

  const [handleNext, nextRequests, navigatingNext] = useNextStep(
    presentStep,
    nextOnClick,
    nextUrl,
    noProgress,
    nextSkip,
    recipients,
  );

  const [handlePrev, prevRequests, navigatingPrev] = usePrevStep(
    presentStep,
    prevOnClick,
    prevUrl,
    prevSkip,
  );

  const navigating = navigatingNext || navigatingPrev;

  const isSaving = saveRequests ? NetworkRequest.anyRunning(...[saveRequests].flat()) : false;

  const actions = (
    <ProgressFooterActions
      nextContent={
        <>
          {navigating && (
            <>
              <InlineLoading />{" "}
            </>
          )}
          {nextLabel}
        </>
      }
      nextOnClick={handleNext}
      nextDisabled={nextDisabled || navigating || isSaving}
      nextDisabledTooltip={isSaving ? "Changes are currently being saved" : nextDisabledTooltip}
      nextTooltip={nextTooltip}
      prevContent={prevLabel}
      prevOnClick={handlePrev}
      prevDisabled={navigating}
      nextLoading={nextRequests.some((r) => r.running)}
      prevLoading={prevRequests.some((r) => r.running)}
    />
  );

  content = content || (saveRequests && <ProgressFooterSaveNotification request={saveRequests} />);

  return <ProgressFooter content={content} actions={actions} />;
};

interface CatchupSurveyFooterProps<T extends string> {
  presentStep: T;
  progressStep: T;
  nextLabel?: string | ReactNode;

  hideNext?: boolean;
  nextOnClick?: () => Promise<void | boolean>;
  nextDisabled?: boolean;
  nextDisabledTooltip?: string;

  noProgress?: boolean;
  prevLabel?: string | ReactNode;
  prevOnClick?: () => Promise<void>;
  noPrev?: boolean;
  content?: ReactNode | string;
  saveRequests?: MutationOrNetworkState[] | MutationOrNetworkState;
}

export const createCatchupSurveyFooterComponent =
  <T extends string>(
    useNextSurveyStep: UseNextSurveyStepHook<T>,
    usePrevSurveyStep: UsePrevSurveyStepHook<T>,
  ) =>
  ({
    presentStep,
    progressStep,
    hideNext,
    nextLabel = "Next",
    nextDisabled,
    nextDisabledTooltip,
    nextOnClick,
    prevLabel = (
      <span className="flex-center-vertical">
        <KeyboardArrowLeft /> Back
      </span>
    ),
    prevOnClick,
    noPrev,
    content,
    saveRequests,
  }: CatchupSurveyFooterProps<T>) => {
    if (typeof prevLabel == "string" || prevLabel instanceof String) {
      prevLabel = (
        <span className="flex-center-vertical">
          <KeyboardArrowLeft /> {prevLabel}
        </span>
      );
    }

    const orgId = usePrimaryOrganizationId();

    const [handleNext, navigatingNext] = useNextSurveyStep(
      orgId,
      presentStep,
      progressStep,
      nextOnClick,
    );

    const [handlePrev, navigatingPrev] = usePrevSurveyStep(orgId, presentStep, prevOnClick);

    const navigating = navigatingNext || navigatingPrev;

    const actions = (
      <ProgressFooterActions
        nextContent={
          <>
            {navigating && (
              <>
                <InlineLoading />{" "}
              </>
            )}
            {nextLabel}
          </>
        }
        nextOnClick={handleNext}
        nextDisabled={nextDisabled || navigating}
        nextDisabledTooltip={nextDisabledTooltip}
        prevContent={noPrev ? undefined : prevLabel}
        prevOnClick={noPrev ? undefined : handlePrev}
        prevDisabled={noPrev ? undefined : navigating}
        hideNext={hideNext}
      />
    );

    content =
      content || (saveRequests && <ProgressFooterSaveNotification request={saveRequests} />);

    return <ProgressFooter content={content} actions={actions} />;
  };

type StepLink = {
  url: string;
  label: string;
  progress?: GetCompliantStep;
};

interface ProgressFooterProgressRequestWrapperProps {
  organizationId: OrganizationPublicId;
  contextProgress: GetCompliantStep;
  currentProgress: GetCompliantStep;
  nextProgress?: GetCompliantStep;
  nextLabel?: string | ReactNode;
  nextUrl?: string;
  nextOnClick?: () => void;
  nextDisabled?: boolean;
  nextDisabledTooltip?: string;
  nextTooltip?: string;
  nextOpenConfirmSignal?: () => void;
  nextConfirmedSignal?: boolean;
  nextConfirmCleanup?: () => void;
  prevLabel?: string | ReactNode;
  prevUrl?: string;
  prevOnClick?: () => void;
  content?: ReactNode | string;
  next?: StepLink;
  prev?: StepLink;
}
/**
 * This is DEPRECATED. Use `GetCompliantFooter` instead wherever possible
 * @deprecated Use `GetCompliantFooter` instead wherever possible
 * @param param0
 * @returns
 */
export const ProgressFooterProgressRequestWrapper = ({
  organizationId,
  currentProgress,
  nextProgress,
  nextLabel,
  nextUrl,
  nextDisabled,
  nextDisabledTooltip,
  nextTooltip,
  nextOnClick,
  nextOpenConfirmSignal = null,
  nextConfirmedSignal,
  nextConfirmCleanup,
  next,
  prev,
  prevLabel = (
    <span className="flex-center-vertical">
      <KeyboardArrowLeft /> {prev?.label || "Back"}
    </span>
  ),
  prevUrl,
  prevOnClick,
  content,
}: ProgressFooterProgressRequestWrapperProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [updateProgressSignal, setUpdateProgressSignal] = useState(false);

  nextLabel = next?.label || nextLabel;
  nextUrl = next?.url || nextUrl;
  nextProgress = next?.progress || nextProgress;
  prevUrl = prev?.url || prevUrl;

  const handleNextClick =
    nextOnClick ?? nextOpenConfirmSignal
      ? nextOpenConfirmSignal
      : () => setUpdateProgressSignal(true);
  const confirmedSignal = nextOpenConfirmSignal ? nextConfirmedSignal : updateProgressSignal;

  const { fetch: updateProgress, request: updateProgressRequest } = useActionRequest({
    api: () => Api.organization.updateGetCompliantProgress(organizationId, nextProgress, false),
    messages: {
      forceError: OrganizationError.UpdateGetCompliantProgress,
    },
    onSuccess: () => {
      dispatch(getCompliantProgressActions.set({ progress: nextProgress, hrProgress: null }));
      history.push(nextUrl);
    },
  });

  const doUpdateProgress = useCallback(() => {
    if (StepComparison.isAfter(nextProgress, currentProgress)) {
      updateProgress();
    } else {
      history.push(nextUrl);
    }
  }, [currentProgress, history, nextProgress, nextUrl, updateProgress]);

  useEffect(() => {
    if (confirmedSignal) {
      doUpdateProgress();
      nextConfirmCleanup && nextConfirmCleanup();
    }
  }, [nextConfirmCleanup, confirmedSignal, doUpdateProgress]);

  const actions = (
    <ProgressFooterActions
      nextContent={
        updateProgressRequest.running ? (
          <>
            <InlineLoading />
            {next?.label || nextLabel}
          </>
        ) : (
          next?.label || nextLabel
        )
      }
      nextOnClick={handleNextClick}
      nextDisabled={nextDisabled || updateProgressRequest.running}
      nextDisabledTooltip={nextDisabledTooltip}
      nextTooltip={nextTooltip}
      prevContent={prevLabel}
      prevOnClick={prevOnClick ? prevOnClick : undefined}
      prevUrl={prevUrl}
      prevDisabled={updateProgressRequest.running}
    />
  );

  return <ProgressFooter content={content} actions={actions} />;
};
